/**
 * Reports Configuration — types mirroring GET /admin/reports/config
 * (API: src/controllers/admin/reportsConfig.ts ReportsConfigDTO). The payload is
 * pure data: per-ReportKey presentation metadata (icon/desc/schedule/panels) lives
 * in the frontend REPORT_META map, not here. `opsManagers` is always an array
 * (possibly empty) for every report.
 */

export type RecipientType = 'TO' | 'CC' | 'BCC';

/** One RptReportRecipient row (Active=1). Exactly one of email / employeeID is set. */
export interface ReportRecipient {
  rptReportRecipientID: number;
  recipientType: RecipientType;
  /** Literal address for a literal recipient; null for an employee recipient. */
  email: string | null;
  employeeID: number | null;
  employeeName: string | null;
  /** literal email, else the active AdminEmployee email, else null (unresolvable → warning). */
  resolvedEmail: string | null;
}

/** A supervisor inside an ops manager's resolved roster. */
export interface RosterSupervisor {
  employeeID: number;
  name: string;
  email: string | null;
  directReports: number;
}

/** One RptReportOpsManager enrollment (Active=1) with its resolved roster. */
export interface ReportOpsManager {
  rptReportOpsManagerID: number;
  employeeID: number;
  name: string;
  supervisorCount: number;
  downlineEmployeeCount: number;
  supervisors: RosterSupervisor[];
  /** Extra addresses copied on this manager's own email. Always an array (possibly empty). */
  ccEmails: string[];
  /** Replaces the report title in this manager's email subject/heading; null = default title. */
  alternateTitle: string | null;
  /** Name shown for this manager in the email subject/heading; null = the employee record's name. */
  displayName: string | null;
  /**
   * Managers on the same report that share a distribution name get ONE combined
   * email instead of one each (e.g. "MF HVAC" = two managers). null = sends its
   * own email. The server canonicalizes case-insensitively to an existing name on
   * the same report, so two spellings of one group never coexist.
   */
  distributionName: string | null;
}

/**
 * One attestation question selected as a trigger on a report (TAS-123).
 *
 * `question` is null and `questionActive` false when the underlying question row
 * has been removed — the selection still comes back so it can be flagged and
 * removed, never silently dropped.
 */
export interface ReportQuestion {
  rptReportQuestionID: number;
  timesheetQuestionsID: number;
  question: string | null;
  questionEs: string | null;
  answerType: number | null;
  order: number | null;
  parentQuestionID: number | null;
  /** null = "any non-empty answer fires". */
  triggerAnswer: string | null;
  questionActive: boolean;
  /** Whether this question escalates up the employee's supervisor chain. */
  notifySupervisorChain: boolean;
}

/** One option in the add-question picker (GET /admin/reports/timesheetQuestions). */
export interface TimesheetQuestionOption {
  timesheetQuestionsID: number;
  question: string;
  questionEs: string | null;
  answerType: number;
  order: number | null;
  active: boolean;
  parentQuestionID: number | null;
  parentQuestionAnswerTrigger: string | null;
  /**
   * Empty exactly when the answer type can only ever be "any answer" (Date and
   * Comments). Drive the value control off this rather than the type number.
   */
  suggestedTriggerValues: string[];
}

/** One role option for the supervisor-chain stop picker (GET /admin/reports/roles). */
export interface ReportRole {
  roleID: number;
  name: string;
  supervisor: boolean;
  active: boolean;
  /** A high count is a strong hint the role is the wrong stop. */
  employeeCount: number;
}

/** dbo.TimesheetQuestions.AnswerType. Mirrors the API enum. */
export enum TimesheetAnswerType {
  YesNo = 1,
  Date = 2,
  ConfirmCheckbox = 3,
  MealConditionalWaiver = 4,
  Comments = 5,
}

/** One RptReport row (paused reports included — the Active flag drives display). */
export interface ReportConfig {
  rptReportID: number;
  reportKey: string;
  name: string;
  active: boolean;
  ccRosterSupervisors: boolean;
  recipients: ReportRecipient[];
  opsManagers: ReportOpsManager[];
  questions: ReportQuestion[];
  /** Master switch for escalating up the employee's supervisor hierarchy. */
  notifySupervisorChain: boolean;
  /** null = no stop role; the walk runs to the top of the chain. */
  supervisorChainStopRoleID: number | null;
}

/** A global RptDeadlineRule (Active=1). localTime is "HH:mm". */
export interface DeadlineRule {
  ruleKey: string;
  offsetDaysFromPayPeriodEnd: number;
  localTime: string;
}

/** Current pay period (header pill + client-side deadline resolution). */
export interface ReportsPayPeriod {
  payPeriodID: number;
  weekNumber: number;
  /** Sent on recentPayPeriods rows; the current-period pill predates it. */
  accountingYear?: number;
  /** "YYYY-MM-DD" */
  startDate: string;
  /** "YYYY-MM-DD" */
  endDate: string;
}

/** Full GET /admin/reports/config payload. */
export interface ReportsConfig {
  settings: Record<string, string>;
  deadlineRules: DeadlineRule[];
  payPeriod: ReportsPayPeriod;
  /**
   * Recent pay periods, newest first, for the on-demand "Pick a period" run option.
   * Optional so an API build that predates it degrades to the This week / Last week
   * scopes rather than breaking the run panel.
   */
  recentPayPeriods?: ReportsPayPeriod[];
  reports: ReportConfig[];
}

/** GET /admin/reports/employeeSearch result row. */
export interface ReportEmployeeResult {
  employeeID: number;
  name: string;
  altEmployeeID: string | null;
  email: string | null;
}
